import React, { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from './ui/Card';
import { Clock, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, formatDistanceToNowStrict, isPast } from 'date-fns';

type SyncJob = {
  id: string;
  type: 'metadata' | 'appointments' | 'patients' | 'quotes' | 'notes';
  enabled: boolean;
  frequency_minutes: number;
  last_run: string | null;
  next_run: string | null;
  status?: 'pending' | 'running' | 'completed' | 'error';
  last_error?: string | null;
  last_error_count?: number | null;
  last_inbound_summary?: string | null;
  last_outbound_summary?: string | null;
};

const jobTypeLabels: Record<SyncJob['type'], string> = {
  metadata: 'Master Data Sync',
  appointments: 'Appointments Sync',
  patients: 'Patients Sync',
  quotes: 'Quotes Sync',
  notes: 'Notes Sync',
};

const MS_PER_MINUTE = 60_000;
const LOG_SUMMARY_LOOKBACK_DAYS = 7;
const LOG_SUMMARY_LIMIT = 120;
const SYNC_JOBS_POLL_MS = 60_000;
const SYNC_JOBS_POLL_ACTIVE_MS = 15_000;

type SyncLogRow = {
  type: string;
  direction: string;
  status: string;
  error: string | null;
  error_count: number | null;
  end_time: string | null;
  total_count: number | null;
  success_count: number | null;
};

function buildJobLogSummaries(logs: SyncLogRow[], jobType: SyncJob['type']) {
  let lastError: string | null = null;
  let lastErrorCount: number | null = null;
  let lastErrorTime: string | null = null;
  let lastSuccessTime: string | null = null;
  let lastInboundSummary: string | null = null;
  let lastOutboundSummary: string | null = null;

  for (const log of logs) {
    const endTime = log.end_time || '';
    const matchesJob = log.type === jobType;
    const matchesMetadataOutbound = jobType === 'metadata' && log.type === 'procedures';

    if (matchesJob) {
      if (log.status === 'success' && !lastSuccessTime) {
        lastSuccessTime = endTime;
      }
      if ((log.status === 'error' || log.status === 'warning') && !lastErrorTime) {
        lastErrorTime = endTime;
        lastError = log.error || null;
        lastErrorCount = log.error_count || null;
      }
      if (log.direction === 'inbound' && !lastInboundSummary && endTime) {
        const dt = format(new Date(endTime), 'MMM d, yyyy HH:mm:ss');
        lastInboundSummary = `Pulled ${log.success_count ?? 0}/${log.total_count ?? 0}${(log.error_count ?? 0) > 0 ? ` (${log.error_count} errors)` : ''} · ${log.status} · ${dt}`;
      }
      if (log.direction === 'outbound' && !lastOutboundSummary && endTime) {
        const dt = format(new Date(endTime), 'MMM d, yyyy HH:mm:ss');
        lastOutboundSummary = `Pushed ${log.success_count ?? 0}/${log.total_count ?? 0}${(log.error_count ?? 0) > 0 ? ` (${log.error_count} errors)` : ''} · ${log.status} · ${dt}`;
      }
    }

    if (matchesMetadataOutbound && log.direction === 'outbound' && !lastOutboundSummary && endTime) {
      const dt = format(new Date(endTime), 'MMM d, yyyy HH:mm:ss');
      lastOutboundSummary = `Pushed ${log.success_count ?? 0}/${log.total_count ?? 0}${(log.error_count ?? 0) > 0 ? ` (${log.error_count} errors)` : ''} · ${log.status} · ${dt}`;
    }
  }

  const showError = Boolean(
    lastErrorTime && (!lastSuccessTime || new Date(lastErrorTime) > new Date(lastSuccessTime))
  );

  return {
    last_error: showError ? lastError : null,
    last_error_count: showError ? lastErrorCount : null,
    last_inbound_summary: lastInboundSummary,
    last_outbound_summary: lastOutboundSummary,
  };
}

const frequencyOptions: Record<SyncJob['type'], Array<{ label: string; value: number }>> = {
  metadata: [
    { label: 'Daily', value: 1440 },
    { label: 'Every 2 days', value: 2880 },
    { label: 'Weekly', value: 10080 },
  ],
  appointments: [
    { label: 'Every 1 minute', value: 1 },
    { label: 'Every 2 minutes', value: 2 },
    { label: 'Every 5 minutes', value: 5 },
    { label: 'Every 15 minutes', value: 15 },
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Hourly', value: 60 },
  ],
  patients: [
    { label: 'Every 15 minutes', value: 15 },
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Hourly', value: 60 },
    { label: 'Every 2 hours', value: 120 },
  ],
  quotes: [
    { label: 'Every 15 minutes', value: 15 },
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Hourly', value: 60 },
  ],
  notes: [
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Hourly', value: 60 },
    { label: 'Every 2 hours', value: 120 },
    { label: 'Every 6 hours', value: 360 },
  ],
};

const calculateNextRun = (minutes: number | null): string | null => {
  if (!minutes || minutes <= 0) return null;
  return new Date(Date.now() + minutes * MS_PER_MINUTE).toISOString();
};

export default function SyncJobsSettings() {
  const queryClient = useQueryClient();
  const [runningJob, setRunningJob] = React.useState<SyncJob['type'] | null>(null);
  const pendingToastIdRef = React.useRef<string | null>(null);
  const progressIntervalRef = React.useRef<any>(null);

  const { data: syncJobs, isLoading } = useQuery({
    queryKey: ['sync-jobs'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('sync_jobs')
        .select('*')
        .eq('user_id', session.user.id)
        .not('type', 'eq', 'appointments-sweep')
        .order('type');

      if (error) throw error;

      const lookbackIso = new Date(Date.now() - LOG_SUMMARY_LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentLogs, error: logsError } = await supabase
        .from('sync_logs')
        .select('type, direction, status, error, error_count, end_time, total_count, success_count')
        .eq('user_id', session.user.id)
        .eq('archived', false)
        .gte('start_time', lookbackIso)
        .order('start_time', { ascending: false })
        .limit(LOG_SUMMARY_LIMIT);

      if (logsError) {
        console.warn('Sync job log summaries unavailable:', logsError.message);
      }

      const logs = (logsError ? [] : (recentLogs || [])) as SyncLogRow[];

      return (data || []).map((job: any) => ({
        ...job,
        ...buildJobLogSummaries(logs, job.type as SyncJob['type']),
      })) as SyncJob[];
    },
    refetchInterval: (query) => (
      query.state.data?.some((job) => job.status === 'running') || runningJob
        ? SYNC_JOBS_POLL_ACTIVE_MS
        : SYNC_JOBS_POLL_MS
    ),
  });

  const updateSyncJob = useMutation({
    mutationFn: async (updates: Partial<SyncJob> & { id: string }) => {
      const { data, error } = await supabase
        .from('sync_jobs')
        .update(updates)
        .eq('id', updates.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
      toast.success('Sync settings updated');
    },
    onError: (error) => {
      console.error('Failed to update sync job:', error);
      toast.error('Failed to update sync settings');
    },
  });

  const manualSyncMutation = useMutation({
    mutationFn: async (jobType: 'metadata' | 'appointments' | 'patients' | 'quotes' | 'notes') => {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error('Not authenticated');

      if (jobType === 'notes') {
        const res = await fetch(`/.netlify/functions/sync-jobs-run?manual=true&user_id=${encodeURIComponent(session.user.id)}&job_type=notes`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          credentials: 'same-origin',
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Notes sync failed: ${res.status} ${t}`);
        }
        return await res.json().catch(() => ({}));
      }
      // Use sync-jobs-run for all job types (appointments, patients, quotes) for consistency
      if (jobType === 'quotes') {
        const res = await fetch(`/.netlify/functions/sync-jobs-run?manual=true&user_id=${encodeURIComponent(session.user.id)}&job_type=quotes`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          credentials: 'same-origin',
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Quotes sync failed: ${res.status} ${t}`);
        }
        return await res.json().catch(() => ({}));
      }
      if (jobType === 'patients') {
        const res = await fetch(`/.netlify/functions/sync-jobs-run?manual=true&user_id=${encodeURIComponent(session.user.id)}&job_type=patients`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          credentials: 'same-origin',
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Patients sync trigger failed: ${res.status} ${t}`);
        }
        return await res.json().catch(() => ({}));
      }
      if (jobType === 'appointments') {
        const res = await fetch(`/.netlify/functions/sync-jobs-run?manual=true&user_id=${encodeURIComponent(session.user.id)}&job_type=appointments`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          credentials: 'same-origin',
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Appointments sync trigger failed: ${res.status} ${t}`);
        }
        return await res.json().catch(() => ({}));
      }

      // For other jobs, use the Supabase edge function trigger
      const { data, error } = await supabase.functions.invoke('manual-sync-trigger', {
        body: { job_type: jobType },
      });

      if (error) throw new Error(error.message ?? 'Sync failed');
      return data;
    },
    onMutate: (jobType: 'metadata' | 'appointments' | 'patients' | 'quotes' | 'notes') => {
      setRunningJob(jobType);
      pendingToastIdRef.current = toast.loading(`${jobTypeLabels[jobType]} sync started…`);
    },
    onSuccess: async (data: any, jobType) => {
      const toastId = pendingToastIdRef.current ?? undefined;
      const label = jobTypeLabels[jobType];

      if (data?.accepted === true && data?.status === 'queued') {
        toast.success(label + ' sync started. Check Sync Logs for progress.', { id: toastId });
        queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['job-execution-logs'] });
        return;
      }

      // sync-jobs-run returns a generic envelope without per-job counts — pull the latest log.
      let successCount = data?.successCount;
      let errorCount = data?.errorCount;
      let jobError = data?.jobError as string | undefined;
      if (
        (typeof successCount !== 'number' && typeof errorCount !== 'number') &&
        (jobType === 'quotes' || jobType === 'appointments' || jobType === 'patients' || jobType === 'notes')
      ) {
        try {
          const session = (await supabase.auth.getSession()).data.session;
          if (session?.user?.id) {
            const { data: latest } = await supabase
              .from('sync_logs')
              .select('status, error, total_count, success_count, error_count, direction')
              .eq('user_id', session.user.id)
              .eq('type', jobType)
              .order('start_time', { ascending: false })
              .limit(4);
            const outbound = (latest || []).find((l: { direction: string }) => l.direction === 'outbound');
            const inbound = (latest || []).find((l: { direction: string }) => l.direction === 'inbound');
            const preferred = outbound || inbound;
            if (preferred) {
              successCount = preferred.success_count ?? 0;
              errorCount = preferred.error_count ?? 0;
              if (preferred.status === 'error' || preferred.status === 'warning') {
                jobError = preferred.error || jobError;
              }
            }
          }
        } catch {
          // keep toast generic if log lookup fails
        }
      }

      successCount = typeof successCount === 'number' ? successCount : 0;
      errorCount = typeof errorCount === 'number' ? errorCount : 0;

      if (jobError) {
        toast.error(`${label} sync completed with errors: ${jobError}`, { id: toastId });
      } else {
        let summary = typeof data?.successCount === 'number' || typeof data?.errorCount === 'number' || successCount > 0 || errorCount > 0
          ? `${label} sync completed · ${successCount} succeeded${errorCount ? ` · ${errorCount} failed` : ''}`
          : `${label} sync completed successfully`;
        // For metadata, poll sync_logs for progress (works for both scheduled and direct runs)
        if (jobType === 'metadata') {
          const logId: string | undefined = data?.log_id as string | undefined;
          // Convert initial loading toast to progress toast and keep updating
          const updateProgressToast = async () => {
            try {
              let log: any = null;
              if (logId) {
                const res = await supabase
                  .from('sync_logs')
                  .select('status, details, total_count, success_count, error_count, end_time, start_time')
                  .eq('id', logId)
                  .maybeSingle();
                log = res.data;
              } else {
                // Fallback: pick most recent running metadata log for current user (check status='running' instead of end_time IS NULL)
                const session = (await supabase.auth.getSession()).data.session;
                if (!session) return;
                const res = await supabase
                  .from('sync_logs')
                  .select('id, status, details, total_count, success_count, error_count, end_time, start_time, type')
                  .eq('direction', 'inbound')
                  .eq('user_id', session.user.id)
                  .eq('type', 'metadata')
                  .eq('status', 'running')
                  .order('start_time', { ascending: false })
                  .limit(1);
                log = Array.isArray(res.data) ? res.data[0] : null;
              }
              if (!log) {
                // If no log found and we have data with totals, the sync completed before polling started
                if (data?.totalsByType && (data?.successCount !== undefined || data?.totalCount !== undefined)) {
                  const totals = data.totalsByType || {};
                  const parts = Object.entries(totals)
                    .filter(([, { total }]: any) => total > 0)
                    .map(([key, { total, success, error }]: any) => {
                      const label = key.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      return `${label} ${success}${error ? ` (${error} errors)` : ''}`;
                    });
                  const finalMsg = parts.length ? `${label} synced: ${parts.join(', ')}` : `${label} completed`;
                  toast.success(finalMsg, { id: toastId });
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                  setRunningJob(null);
                  queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
                  queryClient.invalidateQueries({ queryKey: ['job-execution-logs'] });
                  return;
                }
                // If log not found yet but we have log_id, keep polling (log might not be created yet)
                // Show generic syncing message
                if (logId) {
                  toast.loading(`${label} syncing... (starting)`, { id: toastId });
                }
                return; // Continue polling on next interval
              }
              const status: string = (log as any).status;
              const details: any = (log as any).details || {};
              // Check if log is still running (status='running' OR end_time equals start_time means still running)
              const startTime = (log as any).start_time;
              const endTime = (log as any).end_time;
              const isStillRunning = status === 'running' || (endTime && startTime && endTime === startTime);
              if (isStillRunning) {
                const totals = details?.totalsByType || details?.totals || {};
                const currentPhase = details?.phase || '';
                
                // Define the order of sync steps (must match syncMasterData step order)
                const stepOrder = [
                  { key: 'staff', label: 'Staff' },
                  { key: 'appointment_types', label: 'Appointment Types' },
                  { key: 'appointment_subtypes', label: 'Appointment Subtypes' },
                  { key: 'providers', label: 'Providers' },
                  { key: 'staff_services', label: 'Staff Services' },
                  { key: 'facilities', label: 'Facilities' },
                  { key: 'procedures', label: 'Procedures' },
                  { key: 'hl_calendars', label: 'Calendars' },
                ];
                
                const parts: string[] = [];
                let currentStepIndex = -1;
                
                stepOrder.forEach((step, index) => {
                  const v = totals?.[step.key];
                  const hasData = v && (typeof v.total === 'number' && v.total > 0);
                  
                  if (step.key === currentPhase) {
                    currentStepIndex = index;
                    // Show current step being processed
                    if (hasData) {
                    // Check if there's detailed progress info (for procedures)
                    const progressInfo = details?.progress;
                    if (progressInfo && typeof progressInfo.current === 'number' && typeof progressInfo.total === 'number') {
                      const batchInfo = progressInfo.batch ? ` [Batch ${progressInfo.batch}]` : '';
                      parts.push(`🔄 ${step.label}: ${progressInfo.current}/${progressInfo.total}${batchInfo} (${v.success || 0} ok, ${v.error || 0} err)`);
                    } else {
                      parts.push(`🔄 ${step.label}: ${v.success || 0}/${v.total}`);
                    }
                    } else {
                      parts.push(`🔄 ${step.label}...`);
                    }
                  } else if (hasData) {
                    // Step has data = it's been processed
                    const icon = index < currentStepIndex ? '✅' : (index > currentStepIndex && currentStepIndex >= 0 ? '⏳' : '✅');
                    parts.push(`${icon} ${step.label}: ${v.success || 0}${v.error ? ` (${v.error} errors)` : ''}`);
                  } else if (index < currentStepIndex && currentStepIndex >= 0) {
                    // Step completed but no totals data (completed with 0 items)
                    parts.push(`✅ ${step.label}: 0`);
                  } else if (index > currentStepIndex && currentStepIndex >= 0) {
                    // Step not started yet
                    parts.push(`⏳ ${step.label}...`);
                  }
                });
                
                // If we have a current phase, highlight it
                const msg = currentPhase 
                  ? `${label} - ${parts.length ? parts.join(' • ') : `Syncing ${currentPhase.replace('_', ' ')}...`}`
                  : parts.length 
                    ? `${label} syncing: ${parts.join(', ')}`
                    : `${label} syncing...`;
                toast.loading(msg, { id: toastId });
              } else if (status === 'success' || status === 'warning') {
                const totals = details?.totalsByType || {};
                const parts: string[] = [];
                Object.entries(totals).forEach(([k, v]: any) => {
                  const t = v?.total ?? 0;
                  const s = v?.success ?? t;
                  const e = v?.error ?? 0;
                  const tag = k === 'appointment_types' ? 'Appt Types' : k === 'appointment_subtypes' ? 'Appt Subtypes' : k === 'staff_services' ? 'Staff Services' : k === 'hl_calendars' ? 'Calendars' : k.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                  if (t > 0) parts.push(`${tag} ${s}${e ? ` (${e} errors)` : ''}`);
                });
                const finalMsg = parts.length ? `${label} synced: ${parts.join(', ')}` : `${label} completed`;
                toast.success(finalMsg, { id: toastId });
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
                setRunningJob(null);
                queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
                queryClient.invalidateQueries({ queryKey: ['job-execution-logs'] });
              } else if (status === 'error') {
                toast.error(`${label} failed: ${(log as any).error || 'Unknown error'}`, { id: toastId });
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
                setRunningJob(null);
                queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
                queryClient.invalidateQueries({ queryKey: ['job-execution-logs'] });
              }
            } catch {}
          };
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          // Poll more frequently for better UX
          progressIntervalRef.current = setInterval(updateProgressToast, 1500);
          // Also run immediately
          updateProgressToast();
          // Skip immediate success toast since we'll update via poller
          return;
        }
        toast.success(summary, { id: toastId });
      }
      queryClient.invalidateQueries({ queryKey: ['job-execution-logs'] });
      queryClient.invalidateQueries({ queryKey: ['sync-jobs'] });
    },
    onError: (error: any, jobType) => {
      const label = jobTypeLabels[jobType];
      toast.error(`${label} sync failed: ${error.message || 'Unknown error'}`, {
        id: pendingToastIdRef.current ?? undefined,
      });
    },
    onSettled: (data: any, error: any, jobType) => {
      // For metadata syncs, don't clear the interval here - it's managed in onSuccess polling logic
      // The interval will be cleared when sync completes (success/error) or when polling detects completion
      if (jobType !== 'metadata') {
        setRunningJob(null);
        pendingToastIdRef.current = null;
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
      // For metadata, keep the button in loading state until polling detects completion
    },
  });

  const handleToggleEnabled = async (job: SyncJob) => {
    const enabling = !job.enabled;
    await updateSyncJob.mutateAsync({
      id: job.id,
      enabled: enabling,
      next_run: enabling ? calculateNextRun(job.frequency_minutes) : null,
    });
  };

  const handleFrequencyChange = async (job: SyncJob, minutes: number) => {
    await updateSyncJob.mutateAsync({
      id: job.id,
      frequency_minutes: minutes,
      next_run: job.enabled ? calculateNextRun(minutes) : null,
    });
  };

  const formatNextRun = (nextRun: string | null) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    if (Number.isNaN(date.getTime())) return 'Not scheduled';
    const distance = formatDistanceToNowStrict(date, { addSuffix: true });
    const detail = format(date, 'MMM d, yyyy HH:mm');
    return isPast(date)
      ? `Overdue (${detail}, ${distance.replace('ago', 'past')})`
      : `${distance} (${detail})`;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Sync Jobs</h3>
          <p className="text-sm text-gray-500">Configure automatic data synchronization</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          {syncJobs?.map((job) => (
            <div key={job.id} className="border-t pt-6 first:border-t-0 first:pt-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    {jobTypeLabels[job.type as SyncJob['type']]}
                    {job.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {job.enabled ? (
                      <>
                        <Clock className="w-4 h-4 inline-block mr-1 text-gray-400" />
                        Next run: {formatNextRun(job.next_run)}
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 inline-block mr-1 text-yellow-500" />
                        Sync disabled
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={job.frequency_minutes}
                    onChange={(e) => handleFrequencyChange(job, parseInt(e.target.value, 10))}
                    className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    disabled={!job.enabled}
                  >
                    {frequencyOptions[job.type as SyncJob['type']].map((option: { label: string; value: number }) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={job.enabled}
                        onChange={() => handleToggleEnabled(job)}
                      />
                      <div className={`block w-10 h-6 rounded-full ${
                        job.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                      }`} />
                      <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
                        job.enabled ? 'transform translate-x-4' : ''
                      }`} />
                    </div>
                  </label>
                  <button
                    onClick={() => manualSyncMutation.mutate(job.type)}
                    disabled={(manualSyncMutation.isPending && runningJob === job.type) || job.status === 'running'}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    title="Trigger manual sync"
                  >
                    {manualSyncMutation.isPending && runningJob === job.type ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                {job.last_run && (
                  <p>Last sync: {format(new Date(job.last_run), 'MMM d, yyyy HH:mm:ss')}</p>
                )}
                {job.last_inbound_summary && (
                  <p>{job.last_inbound_summary}</p>
                )}
                {job.last_outbound_summary && (
                  <p>{job.last_outbound_summary}</p>
                )}
                {job.status && job.status !== 'completed' && (
                  <p>Status: {job.status}</p>
                )}
                {job.last_error && (
                  <p className="text-red-600 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>
                      Last run error: {job.last_error}
                      {(() => {
                        const errorCount = job.last_error_count ?? 0;
                        return errorCount > 0 ? (
                          <span> ({errorCount} item{errorCount !== 1 ? 's' : ''} failed)</span>
                        ) : null;
                      })()}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
